import type { CSSProperties } from 'react';
import type { ScreenContentCard } from '@/store/editor/types/screen';

/**
 * Карточка вокруг содержимого экрана (задание stylepack/content-card) —
 * зеркало кабинета схемы сервера
 * backend/src/ai-funnels/block-schema/screen-content-card.ts: перечень тени,
 * границы и умолчания; совпадение держит screen-content-card.ends.spec.ts.
 *
 * Ручной сейв и превью идут мимо схемы, поэтому отрисовка верит только тому,
 * что прошло дверь `readScreenContentCard`: кривое свойство уходит одно, не
 * объект — карточки нет.
 */
export const SCREEN_CONTENT_CARD_SHADOWS = ['none', 'soft'] as const;

export const SCREEN_CONTENT_CARD_BOUNDS = {
  borderWidth: { min: 0, max: 4 },
  radius: { min: 0, max: 40 },
  padding: { min: 0, max: 40 },
  margin: { min: 0, max: 32 },
} as const;

export const SCREEN_CONTENT_CARD_DEFAULTS = {
  padding: 16,
  margin: 12,
} as const;

/** Нет поля — рамка 1 px, скругление карточек темы. */
export const SCREEN_CONTENT_CARD_FALLBACK = {
  borderWidth: 1,
  radius: 16,
} as const;

/** Код цвета — то же правило, что BLOCK_COLOR_PATTERN сервера. */
const COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

type Bound = keyof typeof SCREEN_CONTENT_CARD_BOUNDS;

function color(v: unknown): string | undefined {
  return typeof v === 'string' && COLOR.test(v) ? v : undefined;
}

function px(v: unknown, key: Bound): number | undefined {
  const { min, max } = SCREEN_CONTENT_CARD_BOUNDS[key];
  return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max
    ? v
    : undefined;
}

/** Одна дверь чтения карточки; хранится только годное. */
export function readScreenContentCard(
  raw: unknown,
): ScreenContentCard | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const shadow = SCREEN_CONTENT_CARD_SHADOWS.find((s) => s === r.shadow);
  const out: ScreenContentCard = {
    background: color(r.background),
    borderColor: color(r.borderColor),
    borderWidth: px(r.borderWidth, 'borderWidth'),
    radius: px(r.radius, 'radius'),
    padding: px(r.padding, 'padding'),
    margin: px(r.margin, 'margin'),
    shadow,
  };
  for (const key of Object.keys(out) as (keyof ScreenContentCard)[]) {
    if (out[key] === undefined) delete out[key];
  }
  return out;
}

/** Вид карточки; нет свойства — цвета темы и умолчания выше. */
export function contentCardStyle(card: ScreenContentCard): CSSProperties {
  const width = card.borderWidth ?? SCREEN_CONTENT_CARD_FALLBACK.borderWidth;
  const borderColor =
    card.borderColor ?? 'var(--color-border-primary, #e5e5e5)';
  return {
    background: card.background ?? 'var(--color-bg-surface, #ffffff)',
    border: width > 0 ? `${width}px solid ${borderColor}` : 'none',
    borderRadius:
      card.radius ??
      `var(--radius-card, ${SCREEN_CONTENT_CARD_FALLBACK.radius}px)`,
    padding: card.padding ?? SCREEN_CONTENT_CARD_DEFAULTS.padding,
    margin: card.margin ?? SCREEN_CONTENT_CARD_DEFAULTS.margin,
    ...(card.shadow === 'soft'
      ? { boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)' }
      : {}),
  };
}
